/**
 * Ask the Wiki — verbatim prototype copy (D-ask §1, Inbox.dc.html 89186–113093)
 * and the contract's enums (contracts/BE-ask.md). One place so the view, the
 * store and the tests can never drift apart.
 */

export const ASK_COPY = Object.freeze({
    title: 'Ask the Wiki',
    subtitle: 'Written in plain language from pages you can read. JEV steers every answer; every claim links to its source.',
    newConversation: 'New conversation',
    allScope: 'All categories',
    emptyTitle: 'What do you need to know?',
    emptyDetail: 'Try one of these. Each one shows a different part of how the AI Brain works.',
    pending: 'Reading your question · searching pages you can read · running checks',
    placeholder: 'Ask a question, or tell the AI it got something wrong...',
    send: 'Ask',
    hint: 'Try a follow-up like "that\'s wrong, the matrix says 15%" to see how the Brain handles a correction.',
    showExact: 'Show exact wording',
    hideExact: 'Hide exact wording',
    exactLabel: 'EXACTLY AS WRITTEN ON THE PAGE',
    read: 'READ',
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    markedHelpful: 'Marked helpful',
    lessonSaved: 'Lesson saved',
    showTrace: 'Show how this answer was made',
    hideTrace: 'Hide how this answer was made',
    traceTitle: 'How this answer was made',
    whatWentWrong: 'What went wrong?',
    feedbackPlaceholder: 'Optional: what should the answer have said?',
    feedbackHelper: 'Saved as a lesson. If the same lesson repeats, the Brain proposes a fix for an admin to approve.',
    cancel: 'Cancel',
    sendFeedback: 'Send feedback',
    briefLabel: 'What JEV told the writer',
    rewriteLabel: 'Rewritten clearly, facts kept',
    onThePage: 'ON THE PAGE',
    inTheAnswer: 'IN THE ANSWER',
    learned: 'Learned',
    codeCheck: 'Code check',
    // Not in the prototype (real-build states).
    closestPage: 'Closest page',
    tryAgain: 'Try again',
});

/** `wkSuggest` — Home hero chips and the Ask empty state, in this order. */
export const ASK_SUGGESTIONS = Object.freeze([
    "Can I give 25% off on a 3-year enterprise deal?",
    'How many days a week can I work remotely?',
    'Can I share my MFA code with IT support?',
    "What's our response time for P1 escalations?",
]);

/** 👎 reason chips → the contract enum, in prototype order. */
export const FEEDBACK_REASONS = Object.freeze([
    Object.freeze({ value: 'wrong_fact', label: 'Wrong fact' }),
    Object.freeze({ value: 'wrong_page', label: 'Wrong page' }),
    Object.freeze({ value: 'out_of_date', label: 'Out of date' }),
    Object.freeze({ value: 'missing_info', label: 'Missing info' }),
    Object.freeze({ value: 'tone_or_format', label: 'Tone or format' }),
]);

/** Toasts. The server sends its own `toast`; these are the verbatim fallbacks. */
export const ASK_TOASTS = Object.freeze({
    up: 'Thanks. Saved as a helpful answer, which makes similar answers more confident.',
    downProposal: 'Saved as a lesson. It has come up twice, so a new guardrail was proposed to your Wiki admins.',
    downJoined: 'Saved as a lesson. It joins the proposal already waiting for your Wiki admins.',
    downLesson: 'Saved as a lesson. If the same lesson repeats, the Brain proposes a fix for an admin to approve.',
    handoff: (name) => `Sent to ${name} with this conversation attached.`,
});

/** Error copy for the bare-code `detail` strings the Ask routes raise. */
export const ASK_ERRORS = Object.freeze({
    wiki_question_empty: 'Type a question first.',
    wiki_org_required: 'Ask the Wiki needs an organization.',
    wiki_category_not_found: "That category isn't available to you. Pick another scope and ask again.",
    wiki_page_not_found: "That page isn't available to you. Pick another scope and ask again.",
    wiki_thread_not_found: "This conversation isn't available any more. Ask again to start a new one.",
    wiki_turn_not_found: "This answer isn't available any more.",
    wiki_handoff_no_owner: 'This answer has no owner to send it to.',
    wiki_handoff_self: "You own this page, so there's no one else to send it to.",
    wiki_handoff_not_found: "This conversation isn't available to you.",
    // Normally `{code, message}` (message shown as-is); these cover a bare code.
    wiki_category_assistant_off: "Ask isn't turned on for this category. Ask across the whole Wiki instead.",
    wiki_feature_disabled: "Asking within a category isn't included in your plan. Ask across the whole Wiki instead.",
});

/** Refusals of a SCOPED ask: the scope is dropped so "Try again" asks the whole Wiki. */
export const SCOPE_REFUSALS = Object.freeze(['wiki_category_assistant_off', 'wiki_feature_disabled']);

/** READ-row pill tones (`mood_tone`). */
export const MOOD_TONES = Object.freeze(['ok', 'warn', 'bad']);
